import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, FolderOpen, Trash2, Calendar, Globe } from "lucide-react";
import { projectsApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    projectsApi.list().then(setProjects).catch(() => toast.error("Erreur de chargement"))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const remove = async (id) => {
    try {
      await projectsApi.remove(id);
      toast.success("Projet supprimé");
      load();
    } catch { toast.error("Erreur"); }
  };

  return (
    <div className="px-6 lg:px-10 py-10 max-w-7xl mx-auto w-full" data-testid="projects-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-mono text-[10px] text-muted-foreground">ESPACE DE TRAVAIL</div>
          <h1 className="mt-1 font-serif font-bold text-4xl">Mes Projets</h1>
          <p className="mt-2 text-muted-foreground text-sm max-w-xl">
            Chaque projet correspond à un pays et une période d'analyse.
            Importez-y vos documents et lancez les six modules d'analyse.
          </p>
        </div>
        <Button asChild className="h-11 bg-marine hover:bg-marine/90 text-white" data-testid="new-project-btn">
          <Link to="/app/projects/new"><Plus size={16} className="mr-2" /> Nouveau projet</Link>
        </Button>
      </div>

      <div className="mt-10">
        {loading ? (
          <div className="label-mono text-xs text-muted-foreground">Chargement…</div>
        ) : projects.length === 0 ? (
          <div className="border border-dashed border-border rounded-sm p-16 text-center" data-testid="projects-empty">
            <FolderOpen size={36} className="mx-auto text-muted-foreground" />
            <h3 className="mt-4 font-serif text-xl">Aucun projet pour l'instant</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Créez votre premier projet pour lancer l'analyse de la dette d'un pays.
            </p>
            <Button asChild className="mt-6 bg-marine hover:bg-marine/90 text-white">
              <Link to="/app/projects/new"><Plus size={16} className="mr-2" /> Nouveau projet</Link>
            </Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {projects.map((p) => (
              <div key={p.id} className="border border-border rounded-sm bg-card p-6 hover-lift hover:border-gold/60 transition"
                   data-testid={`project-card-${p.id}`}>
                <div className="flex justify-between items-start">
                  <div className="label-mono text-[9px] text-gold">{p.country || "—"}</div>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button className="text-muted-foreground hover:text-alert transition p-1"
                              data-testid={`project-delete-btn-${p.id}`} title="Supprimer">
                        <Trash2 size={14} />
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Supprimer « {p.name} » ?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Cette action supprime définitivement le projet, ses documents et analyses.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction onClick={() => remove(p.id)} className="bg-alert hover:bg-alert/90">
                          Supprimer
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
                <Link to={`/app/projects/${p.id}`} className="block mt-2">
                  <h3 className="font-serif font-bold text-xl truncate">{p.name}</h3>
                  <p className="text-xs text-muted-foreground line-clamp-2 mt-2">{p.description || "Aucune description"}</p>
                  <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><Calendar size={11} /> {p.period_start}–{p.period_end}</span>
                    <span className="flex items-center gap-1"><Globe size={11} /> {p.currency}</span>
                  </div>
                  <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                    <KpiMini label="Sj" value={p.daily_wage} suffix="" />
                    <KpiMini label="Pop. act." value={p.active_population} />
                    <KpiMini label="Contrib." value={p.taxpayers} />
                  </div>
                </Link>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function KpiMini({ label, value, suffix }) {
  return (
    <div className="border border-border bg-background/50 rounded-sm p-2">
      <div className="label-mono text-[8px] text-muted-foreground">{label}</div>
      <div className="font-mono text-xs tabular-nums mt-0.5 truncate">
        {value?.toLocaleString("fr-FR") || "—"}{suffix || ""}
      </div>
    </div>
  );
}
